import * as React from 'react'
import { useState, useMemo } from 'react'
import { View, ScrollView, StyleSheet } from 'react-native'
import { Text, IconButton, TextInput } from 'react-native-paper'
import { StackScreenProps } from '@react-navigation/stack'
import { useNavigation } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useGlobalStyles } from '../../contexts/global-styles.provider'
import {
  useGetNodeTierConfigQuery,
  TierConfigDto,
} from '../../generated/graphql'
import { AppNavigatorParams, AppNavigation } from '../../navigation/types'
import { Row, MelddButton, Icon } from '../../@ui'
import { useCurrency } from '../node-admin/payment/payment.utils'
import { useAuthContext } from '../../contexts/auth-context'
import { createStyle } from '../../contexts/global-styles.provider'
import { useIsMobileDevice } from '../../hooks/is-mobile.hook'
import { MelddRawCard } from '../../@ui/containers/cards/meldd-raw.card'
import { TouchableIconWithToolTip } from '../../utils/meldd-tooltip'
import { ComparisonTable } from './components/comparison-table.component'

const PricingCard = ({
  title,
  description,
  isPrivate,
  selectedSeats,
  onSeatsChange,
}: {
  title: string
  description: string
  isPrivate: boolean
  selectedSeats: { seats: number }
  onSeatsChange: (seats: { seats: number }) => void
}) => {
  const { t } = useTranslation('tiers')
  const styles = useStyle()
  const seatPrice = isPrivate ? 5 : 1
  const totalPrice = selectedSeats.seats * seatPrice
  const currency = useCurrency(totalPrice)
  const [seatInput, setSeatInput] = useState(selectedSeats.seats.toString())

  return (
    <MelddRawCard
      height={500}
      header={
        <View style={styles.cardPadding}>
          <Text variant="titleLarge">{title}</Text>
        </View>
      }
    >
      <View style={styles.cardPadding}>
        <View style={{ gap: 20 }}>
          <Text variant="bodyMedium">{description}</Text>
          <Row
            justifyContent="space-between"
            style={{ flex: 1 }}
            alignItems="center"
          >
            <Row alignItems="center" columnGap={4}>
              <TouchableIconWithToolTip
                icon={<Icon source="seat" size={24} />}
                tooltip={t('core')}
              />
              <Text variant="titleMedium">{t('seats')}</Text>
            </Row>
            <Row alignItems="center" columnGap={4} style={{ paddingTop: 10 }}>
              <TextInput
                mode="outlined"
                value={seatInput}
                keyboardType="number-pad"
                maxLength={4}
                style={{ width: 72, height: 40 }}
                onChangeText={(value) => {
                  const digits = value.replace(/[^0-9]/g, '')
                  setSeatInput(digits)
                  const parsedSeats = Number(digits)
                  if (!digits || parsedSeats <= 0) {
                    return
                  }
                  onSeatsChange({ seats: parsedSeats })
                }}
              />
            </Row>
          </Row>
          <View style={styles.priceContainer}>
            <Text variant="titleLarge">
              {t('price')}: {currency} {t('by-month')}
            </Text>
          </View>
        </View>
      </View>
    </MelddRawCard>
  )
}

export function PricingScreen({
  navigation,
}: StackScreenProps<AppNavigatorParams, 'Pricing'>) {
  const globalStyles = useGlobalStyles()
  const { t } = useTranslation('pricing')
  const { data, loading } = useGetNodeTierConfigQuery()
  const isMobile = useIsMobileDevice()

  const publicTiers = useMemo(
    () =>
      data?.nodeTierConfig?.tiers.filter(
        (tier) => tier.layout === 'default' && tier.visibility === 'public'
      ),
    [data]
  )

  const privateTiers = useMemo(
    () =>
      data?.nodeTierConfig?.tiers.filter(
        (tier) => tier.layout === 'default' && tier.visibility === 'private'
      ),
    [data]
  )

  const publicTier = publicTiers?.[0]
  const privateTier = privateTiers?.[0]

  const [publicSeats, setPublicSeats] = useState<{
    seats: number
  }>({ seats: 10 })
  const [privateSeats, setPrivateSeats] = useState<{
    seats: number
  }>({ seats: 10 })

  const { authAction } = useAuthContext()
  const nav = useNavigation<AppNavigation>()

  const handleCreateNode = (isPrivate: boolean, seats: number) => {
    authAction(nav, () => {
      navigation.navigate('NodeSettings', {
        nodeId: '',
        isPrivate,
        seats,
      })
    })
  }

  if (loading || !data?.nodeTierConfig) {
    return (
      <View style={[globalStyles.pageContainer, styles.container]}>
        <Text>{t('loading')}</Text>
      </View>
    )
  }
  return (
    <ScrollView
      style={[globalStyles.pageContainer]}
      contentContainerStyle={styles.scrollContent}
    >
      <View style={styles.header}>
        <Row alignItems="center" columnGap={8}>
          <IconButton
            mode={'outlined'}
            icon="arrow-left"
            onPress={() => navigation.goBack()}
          />
          <View style={styles.headerText}>
            <Text variant="headlineMedium">{t('title')}</Text>
            <Text variant="bodyLarge" style={styles.description}>
              {t('description')}
            </Text>
          </View>
        </Row>
      </View>

      <Row style={styles.cardsContainer} columnGap={24} wrap={true}>
        {publicTier && publicSeats && (
          <View
            style={isMobile ? styles.cardWrapperMobile : styles.cardWrapper}
          >
            <PricingCard
              title={t('public-node-title')}
              description={t('public-node-description')}
              isPrivate={false}
              selectedSeats={publicSeats}
              onSeatsChange={(seats) => setPublicSeats(seats)}
            />
            <View style={styles.buttonWrapper}>
              <MelddButton
                onPress={() => handleCreateNode(false, publicSeats?.seats || 0)}
                title={t('create-your-node')}
                height={36}
                fontSize={14}
                style={styles.createButton}
              />
            </View>
          </View>
        )}

        {privateTier && privateSeats && (
          <View
            style={isMobile ? styles.cardWrapperMobile : styles.cardWrapper}
          >
            <PricingCard
              title={t('private-node-title')}
              description={t('private-node-description')}
              isPrivate={true}
              selectedSeats={privateSeats}
              onSeatsChange={(seats) => setPrivateSeats(seats)}
            />
            <View style={styles.buttonWrapper}>
              <MelddButton
                onPress={() => handleCreateNode(true, privateSeats?.seats || 0)}
                title={t('create-your-node')}
                height={36}
                fontSize={14}
                style={styles.createButton}
              />
            </View>
          </View>
        )}
      </Row>

      <ComparisonTable />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  header: {
    marginBottom: 16,
  },
  headerText: {
    flex: 1,
    gap: 8,
  },
  description: {
    opacity: 0.7,
  },
  cardsContainer: {
    flex: 1,
  },
  cardWrapper: {
    flex: 1,
  },
  cardWrapperMobile: {
    width: '100%',
    minWidth: '100%',
  },
  buttonWrapper: {
    marginTop: 16,
    paddingHorizontal: 4,
  },
  createButton: {
    borderRadius: 20,
  },
})

const useStyle = createStyle(({ theme }) => ({
  cardPadding: {
    paddingHorizontal: theme.spacing(4),
    paddingVertical: theme.spacing(2),
  },
  priceContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
}))

export default PricingScreen
